import React, { useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SiteFooter from "../../general/components/layout/SiteFooter";
import SiteNavbar from "../../general/components/layout/SiteNavbar";
import useRevealOnScroll from "../../general/components/common/useRevealOnScroll";
import Temarios, { getTemarioUnitCount } from "../components/Temarios";
import { getEnrollmentPath, resolveCourse } from "../components/courseRouteUtils";
import { courseSpecificFaqs, generalFaqGroups } from "../assets/faqData";
import fispLogo from "../../general/assets/logo-iniciales-oscuro.png";
import ubaLogo from "../assets/media/img/UBA.jpg";

const TYPE_CONFIG = {
    Curso: {
        categoryParam: "curso",
        categoryLabel: "Cursos",
        singularLabel: "curso",
        aboutLabel: "Sobre el curso",
    },
    Diplomatura: {
        categoryParam: "diplomatura",
        categoryLabel: "Diplomaturas",
        singularLabel: "diplomatura",
        aboutLabel: "Sobre la diplomatura",
    },
    Posgrado: {
        categoryParam: "posgrado",
        categoryLabel: "Posgrados",
        singularLabel: "posgrado",
        aboutLabel: "Sobre el posgrado",
    },
};

const formatCurrency = (amount, currency = "ARS") => {
    if (!amount) {
        return null;
    }

    if (currency === "USD") {
        return `USD ${Number(amount).toLocaleString("es-AR")}`;
    }

    return `$${Number(amount).toLocaleString("es-AR")}`;
};

const shouldNormalizeText = (value) =>
    typeof value === "string" && /[\u00C3\u00C2\u00E2]/.test(value);

const normalizeText = (value) => {
    if (!shouldNormalizeText(value)) {
        return value;
    }

    try {
        const bytes = Uint8Array.from(value, (char) => char.charCodeAt(0));
        const decoded = new TextDecoder("utf-8").decode(bytes);

        return decoded.includes("\uFFFD") ? value : decoded;
    } catch {
        return value;
    }
};

const normalizeList = (items) =>
    Array.isArray(items) ? items.map((item) => normalizeText(item)) : [];

const isHybridModality = (modalidad) => {
    const cleanModality = normalizeText(modalidad || "").toLowerCase();

    return cleanModality.includes("presencial") &&
        (cleanModality.includes("virtual") || cleanModality.includes("online"));
};

const getFichaDurationLabel = (curso) => {
    if (curso.duracion) {
        return `${curso.duracion} ${Number(curso.duracion) === 1 ? "mes" : "meses"}`;
    }

    if (curso.clases) {
        return `${curso.clases} ${Number(curso.clases) === 1 ? "clase" : "clases"}`;
    }

    return "A definir";
};

const getUnitLabel = (unitCount) => {
    if (!unitCount) {
        return "A definir";
    }

    return `${unitCount} ${unitCount === 1 ? "unidad" : "unidades"}`;
};

const getFichaModalityLabel = (curso) => {
    const modality = normalizeText(curso.modalidad || "");

    if (isHybridModality(modality)) {
        return "Virtual + presencial";
    }

    if (/virtual|online|asincr/i.test(modality)) {
        return "100% Online";
    }

    return modality || "A definir";
};

const cleanScheduleLabel = (schedule) =>
    normalizeText(schedule || "")
        .replace(/\s*\(quincenal\)\s*/i, "")
        .replace(/\bde\s+(\d{1,2}\s*a\s*\d{1,2}\s*hs)/i, "$1")
        .replace(/\s+/g, " ")
        .trim();

const getFichaScheduleLabel = (curso, isAsync) => {
    if (isAsync) {
        return "A tu ritmo";
    }

    return cleanScheduleLabel(curso.diasHorarios) || "A definir";
};

const getFrequencyLabel = (curso, isAsync) => {
    if (isAsync) {
        return "A tu ritmo";
    }

    const schedule = cleanScheduleLabel(curso.diasHorarios).toLowerCase();
    const modality = normalizeText(curso.modalidad || "").toLowerCase();

    if (schedule.includes("intensiv") || modality.includes("intensiv")) {
        return "Cronograma intensivo";
    }

    return curso.quincenal ? "Clases quincenales" : "Clases semanales";
};

const getAcademicHoursLabel = (curso) => {
    if (curso.horas) {
        return `${curso.horas} hs cátedra`;
    }

    if (curso.clases) {
        return `${curso.clases} ${Number(curso.clases) === 1 ? "clase" : "clases"}`;
    }

    return "A definir";
};

const getScheduleSentence = (curso) => {
    const schedule = cleanScheduleLabel(curso.diasHorarios);

    if (!schedule) {
        return "en horarios programados";
    }

    const lowerSchedule =
        schedule.charAt(0).toLocaleLowerCase("es-AR") + schedule.slice(1);

    if (/^(lunes|martes|miércoles|miercoles|jueves|viernes|sábado|sabado|sábados|sabados|domingo)/i.test(schedule)) {
        return `los ${lowerSchedule}`;
    }

    return lowerSchedule;
};

const getCourseMethodCards = (curso, isAsync) => {
    if (isAsync) {
        return [
            {
                icon: "📺",
                title: "Contenido grabado",
                text: "Accedés a los videos y materiales en cualquier momento, sin horarios fijos ni clases en vivo.",
            },
            {
                icon: "🧮",
                title: "A tu ritmo",
                text: "Sin fecha de inicio fija. Avanzás según tu disponibilidad y el contenido queda disponible en plataforma.",
            },
        ];
    }

    const area = getAreaLabel(curso).split(" · ")[0].toLocaleLowerCase("es-AR");

    return [
        {
            icon: "🎥",
            title: "Clases en vivo",
            text: `Encuentros ${curso.quincenal ? "quincenales" : "semanales"} ${getScheduleSentence(curso)} con especialistas en ${area}.`,
        },
        {
            icon: "📖",
            title: `${curso.horas || 170} horas cátedra`,
            text: "Exámenes, bibliografía, trabajos prácticos y contenido en plataforma virtual.",
        },
        {
            icon: "💻",
            title: "100% Online",
            text: "Cursada completamente virtual con contenido en plataforma disponible 24/7.",
        },
    ];
};

const getResolvedCourseMethodCards = (curso, isAsync) => {
    if (isHybridModality(curso.modalidad)) {
        return [
            {
                icon: "🎥",
                title: "Clases virtuales intensivas",
                text: "Contenido teórico online con acompañamiento docente y material de estudio en plataforma.",
            },
            {
                icon: "🤝",
                title: "Práctica presencial",
                text: curso.practicaFinal
                    ? `Encuentro práctico presencial previsto para ${curso.practicaFinal}.`
                    : "Instancia práctica presencial complementaria según el cronograma del programa.",
            },
            {
                icon: "🔒",
                title: "Grupos limitados",
                text: "Cupos reducidos para favorecer una capacitación personalizada y supervisión cercana.",
            },
        ];
    }

    return getCourseMethodCards(curso, isAsync);
};

const getScheduleLabel = (curso) => {
    if (curso.diasHorarios) {
        return curso.diasHorarios;
    }

    if (curso.modalidad?.toLowerCase().includes("asincr")) {
        return "A tu ritmo";
    }

    return "Campus virtual";
};

const getRhythmLabel = (curso) => {
    const modality = normalizeText(curso.modalidad || "").toLowerCase();

    if (modality.includes("asincr")) {
        return "A tu tiempo";
    }

    return getScheduleLabel(curso);
};

const AREA_LABELS = {
    criminalistica: "Criminalística",
    dermatologia: "Dermatología",
    emergencias: "Emergencias",
    enfermeria: "Enfermería",
    ginecologia: "Ginecología",
    marketing: "Marketing",
    "medicina-de-precision": "Medicina de precisión",
    neurologia: "Neurología",
    nutricion: "Nutrición",
    psiquiatria: "Psiquiatría",
    "salud-mental-y-adicciones": "Salud mental",
    tecnologia: "Tecnología",
};

const getAreaLabel = (curso) => {
    const explicitArea = normalizeText(curso.area || "");

    if (explicitArea) {
        return explicitArea;
    }

    const labels = (curso.categoria || [])
        .map((category) => AREA_LABELS[category] || normalizeText(category))
        .filter(Boolean);
    const courseName = normalizeText(curso.nombre || "");

    if (/inmunizaciones/i.test(courseName) && !labels.includes("Vacunación")) {
        labels.push("Vacunación");
    }

    return labels.length ? labels.join(" · ") : "Salud";
};

const getShortModalityLabel = (modalidad) => {
    const cleanModality = normalizeText(modalidad || "");
    const lowerModality = cleanModality.toLowerCase();

    if (isHybridModality(cleanModality)) {
        return "Virtual + presencial";
    }

    if (lowerModality.includes("asincr")) {
        return "Asincrónica";
    }

    if (lowerModality.includes("virtual")) {
        return "Virtual";
    }

    return cleanModality || "A definir";
};

const getCertificationLabel = (titulacion) => {
    const cleanTitle = normalizeText(titulacion || "");

    if (/uba|universidad de buenos aires/i.test(cleanTitle)) {
        return "FISP + UBA";
    }

    if (/fisp/i.test(cleanTitle)) {
        return "FISP";
    }

    return cleanTitle || "A definir";
};

const getDirectedByLabel = (names) => {
    const firstName = names[0];

    if (!firstName) {
        return null;
    }

    const normalizedName = normalizeText(firstName);
    const lowerName = normalizedName.toLowerCase();
    const article = lowerName.startsWith("dra.") ? "la " : lowerName.startsWith("dr.") ? "el " : "";

    return `Dirigido por ${article}${normalizedName}`;
};

const getInitials = (name) => {
    const parts = normalizeText(name || "")
        .replace(/^(dr\.?|dra\.?|lic\.?|mg\.?|prof\.?)\s+/i, "")
        .split(/\s+/)
        .filter(Boolean);

    if (parts.length <= 1) {
        return (parts[0] || "").slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const splitParentheticalNote = (value) => {
    const normalizedValue = normalizeText(value || "");
    const noteMatch = normalizedValue.match(/^(.*?)(\s*\([^)]*\))\s*$/);

    if (!noteMatch) {
        return { main: normalizedValue, note: "" };
    }

    return {
        main: noteMatch[1].trim(),
        note: noteMatch[2].trim(),
    };
};

const normalizePeople = (items) =>
    Array.isArray(items)
        ? items
              .map((item) => {
                  if (typeof item === "string") {
                      return { nombre: normalizeText(item), detalle: "" };
                  }

                  return {
                      titulo: normalizeText(item?.titulo),
                      nombre: normalizeText(item?.nombre),
                      detalle: normalizeText(item?.detalle),
                  };
              })
              .filter((item) => item.nombre)
        : [];

const normalizeCertifications = (items) =>
    Array.isArray(items)
        ? items
              .map((item) => ({
                  certificacion: normalizeText(item?.certificacion),
                  aplicaA: normalizeText(item?.aplicaA),
              }))
              .filter((item) => item.certificacion)
        : [];

const normalizeFactGroups = (items) =>
    Array.isArray(items)
        ? items
              .map((item) =>
                  Array.isArray(item)
                      ? item.map((value) => normalizeText(value))
                      : null
              )
              .filter(Boolean)
        : [];

const normalizeMethodCards = (items) =>
    Array.isArray(items)
        ? items
              .map((item) =>
                  item
                      ? {
                            icon: normalizeText(item.icon),
                            title: normalizeText(item.title),
                            text: normalizeText(item.text),
                        }
                      : null
              )
              .filter((item) => item?.title && item?.text)
        : [];

function CourseFaq({ groups }) {
    const [openFaqId, setOpenFaqId] = useState(null);

    return (
        <section className="course-faq reveal">
            <div className="course-faq-inner">
                <div className="course-faq-head">
                    <span>FAQ</span>
                    <h2>Preguntas frecuentes</h2>
                    <p>Respuestas a las consultas más comunes.</p>
                </div>

                <div className="course-faq-accordion">
                    {groups.map((group, groupIndex) => (
                        <div className="course-faq-group" key={group.title}>
                            <h3>{group.title}</h3>
                            {group.items.map((item, itemIndex) => {
                                const faqId = `course-faq-${groupIndex}-${itemIndex}`;
                                const isOpen = openFaqId === faqId;

                                return (
                                    <article
                                        className={`course-faq-item${isOpen ? " is-open" : ""}`}
                                        key={item.question}
                                    >
                                        <button
                                            type="button"
                                            className="course-faq-summary"
                                            onClick={() => setOpenFaqId(isOpen ? null : faqId)}
                                            aria-expanded={isOpen}
                                            aria-controls={`${faqId}-answer`}
                                        >
                                            <span>{item.question}</span>
                                            <i aria-hidden="true" />
                                        </button>
                                        <div
                                            id={`${faqId}-answer`}
                                            className="course-faq-answer"
                                            aria-hidden={!isOpen}
                                        >
                                            <p>{item.answer}</p>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default function Curso({ family }) {
    const { slug } = useParams();
    const pageRef = useRef(null);
    useRevealOnScroll(pageRef);

    const curso = useMemo(() => resolveCourse(family, slug), [family, slug]);

    if (!curso) {
        return <Navigate to="/educativo" replace />;
    }

    const displayCourse = {
        ...curso,
        nombre: normalizeText(curso.nombre),
        titulacion: normalizeText(curso.titulacion),
        area: normalizeText(curso.area),
        descripcion: normalizeText(curso.descripcion),
        descripcionPreview: normalizeText(curso.descripcionPreview),
        descripcionExtra: normalizeList(curso.descripcionExtra),
        sobreTitulo: normalizeText(curso.sobreTitulo),
        objetivos: normalizeList(curso.objetivos),
        sistemaEvaluacion: normalizeList(curso.sistemaEvaluacion),
        destinatarioTags: normalizeList(curso.destinatarioTags),
        director: normalizeList(curso.director),
        directorDetalle: normalizeList(curso.directorDetalle),
        direccionAcademica: normalizePeople(curso.direccionAcademica),
        codireccionAcademica: normalizePeople(curso.codireccionAcademica),
        equipoDocente: normalizePeople(curso.equipoDocente),
        certificaciones: normalizeCertifications(curso.certificaciones),
        inicio: normalizeText(curso.inicio),
        fin: normalizeText(curso.fin),
        practicaFinal: normalizeText(curso["practica final"]),
        diasHorarios: normalizeText(curso.diasHorarios),
        destinatarios: normalizeText(curso.destinatarios),
        requisitos: normalizeList(curso.requisitos),
        modalidad: normalizeText(curso.modalidad),
        cuotas: normalizeText(curso.cuotas),
        quincenal: Boolean(curso.quincenal),
    };
    const coursePage = {
        heroBadge: normalizeText(curso.pageContent?.heroBadge),
        dataCardTitle: normalizeText(curso.pageContent?.dataCardTitle),
        heroFacts: normalizeFactGroups(curso.pageContent?.heroFacts),
        infoBarItems: normalizeList(curso.pageContent?.infoBarItems),
        aboutTag: normalizeText(curso.pageContent?.aboutTag),
        aboutTitle: normalizeText(curso.pageContent?.aboutTitle),
        objectivesTag: normalizeText(curso.pageContent?.objectivesTag),
        audienceTag: normalizeText(curso.pageContent?.audienceTag),
        methodCards: normalizeMethodCards(curso.pageContent?.methodCards),
        showAudienceTags: Boolean(curso.pageContent?.showAudienceTags),
        sidebarFactsTitle: normalizeText(curso.pageContent?.sidebarFactsTitle),
        sidebarFacts: normalizeFactGroups(curso.pageContent?.sidebarFacts),
        sidebarNote: normalizeText(curso.pageContent?.sidebarNote),
        certificationTitle: normalizeText(curso.pageContent?.certificationTitle),
        certificationDescription: normalizeText(
            curso.pageContent?.certificationDescription
        ),
    };
    const typeConfig = TYPE_CONFIG[curso.tipo] || TYPE_CONFIG.Curso;
    const dataCardTitle =
        coursePage.dataCardTitle ||
        (typeConfig.singularLabel === "diplomatura"
            ? "Datos de la diplomatura"
            : `Datos del ${typeConfig.singularLabel}`);
    const finalCtaArticle =
        typeConfig.singularLabel === "diplomatura" ? "la" : "el";
    const isSoldOut = Boolean(curso.cuposLlenos);
    const hasEnrollmentStatusChip =
        curso.tipo === "Diplomatura" || curso.tipo === "Posgrado";
    const showEnrollmentStatusChip = hasEnrollmentStatusChip;
    const showHeroChips = !isSoldOut;
    const heroPrimaryChipLabel =
        coursePage.heroBadge ||
        (hasEnrollmentStatusChip ? "Inscripciones abiertas 2026" : curso.tipo);
    const breadcrumbLink = `/educativo?categoria=${typeConfig.categoryParam}&scroll=catalogo`;
    const enrollmentPath = getEnrollmentPath(curso);
    const themeStyle = {
        "--course-primary": curso.theme.primary,
        "--course-primary-dark": curso.theme.primaryDark,
        "--course-primary-light": curso.theme.primaryLight,
        "--course-pale": curso.theme.pale,
        "--course-background": curso.theme.background,
        "--course-navy": curso.theme.navy,
        "--course-navy-dark": curso.theme.navyDark,
        "--course-hero-start": curso.theme.heroStart,
        "--course-hero-mid": curso.theme.heroMid,
        "--course-hero-end": curso.theme.heroEnd,
    };
    const directorPeople = displayCourse.direccionAcademica.length
        ? displayCourse.direccionAcademica
        : (displayCourse.director || [])
              .filter(Boolean)
              .map((name, index) => ({
                  nombre: name,
                  detalle: displayCourse.directorDetalle?.[index] || "",
              }));
    const directorNames = directorPeople.map((person) => person.nombre).filter(Boolean);
    const codirectorPeople = displayCourse.codireccionAcademica;
    const facultyPeople = displayCourse.equipoDocente;
    const certificationOptions = displayCourse.certificaciones;
    const temarioUnitCount = getTemarioUnitCount(curso.temario, curso);
    const courseUnitCount = displayCourse.unidades || temarioUnitCount;
    const certificationLabel = getCertificationLabel(displayCourse.titulacion);
    const isFispUbaCertification = certificationLabel === "FISP + UBA";
    const infoBarModality = getShortModalityLabel(displayCourse.modalidad);
    const isAsyncModality = infoBarModality.toLowerCase().includes("asincr");
    const defaultFichaFacts = [
        ["Programa", curso.tipo],
        ["Área", getAreaLabel(curso)],
        ["Duración", getFichaDurationLabel(displayCourse)],
        ["Unidades", getUnitLabel(courseUnitCount)],
        ["Modalidad", getFichaModalityLabel(displayCourse)],
        ["Inicio", displayCourse.inicio || (isAsyncModality ? "Sin fecha fija" : "A definir")],
        ["Finalización", displayCourse.fin || (isAsyncModality ? "A tu ritmo" : "A definir")],
        displayCourse.practicaFinal
            ? ["Práctica final", displayCourse.practicaFinal]
            : null,
        ["Horario", getFichaScheduleLabel(displayCourse, isAsyncModality)],
        ["Frecuencia", getFrequencyLabel(displayCourse, isAsyncModality)],
        ["Carga horaria", getAcademicHoursLabel(displayCourse)],
    ].filter(Boolean);
    const fichaFacts = coursePage.sidebarFacts.length
        ? coursePage.sidebarFacts
        : defaultFichaFacts;
    const heroFacts = coursePage.heroFacts.length
        ? coursePage.heroFacts
        : [
        ["🎓", "Certificación", getCertificationLabel(displayCourse.titulacion)],
        ["Ⅱ", "Modalidad", getShortModalityLabel(displayCourse.modalidad)],
        courseUnitCount ? ["📚", "Unidades", `${courseUnitCount} unidades`] : null,
        ["⏱️", "Horario", getRhythmLabel(displayCourse)],
        isHybridModality(displayCourse.modalidad)
            ? ["🤝", "Práctica", "Instancia presencial"]
            : ["💻", "Online", "100% online"],
    ].filter(Boolean);
    const methodCards = coursePage.methodCards.length
        ? coursePage.methodCards
        : getResolvedCourseMethodCards(displayCourse, isAsyncModality);
    const infoBarItems = coursePage.infoBarItems.length
        ? coursePage.infoBarItems
        : [
        getDirectedByLabel(directorNames) || displayCourse.titulacion,
        `Certificación ${getCertificationLabel(displayCourse.titulacion)}`,
        `Modalidad ${infoBarModality.toLowerCase()}${
            isAsyncModality ? " · Cursa a tu ritmo" : ""
        }`,
    ].filter(Boolean);
    const hasExtraDescription =
        Array.isArray(displayCourse.descripcionExtra) &&
        displayCourse.descripcionExtra.length > 0;
    const hasObjectives =
        Array.isArray(displayCourse.objetivos) &&
        displayCourse.objetivos.length > 0;
    const evaluationItems =
        displayCourse.sistemaEvaluacion.length > 0
            ? displayCourse.sistemaEvaluacion
            : ["Teórico", "Examen Parcial", "Examen Final"];
    const hasPricing =
        Boolean(curso.precioTransferencia) ||
        Boolean(curso.precioMP) ||
        Boolean(curso.precioTransferenciaUSA) ||
        (displayCourse.cuotas && curso.precioSuscripcionMP);
    const showCourseMedia = curso.hasMediaAssets && Boolean(curso.foto);
    const showAudienceTags =
        coursePage.showAudienceTags && displayCourse.destinatarioTags.length > 0;
    const directorImage =
        Array.isArray(curso.directorImg) && curso.directorImg.length > 0
            ? curso.directorImg.find(Boolean)
            : null;
    const certificationTitle =
        coursePage.certificationTitle || `Certificación ${certificationLabel}`;
    const certificationDescription =
        coursePage.certificationDescription ||
        (isFispUbaCertification
            ? "Certificado de la Fundación Iberoamericana de Salud Pública y la Universidad de Buenos Aires."
            : "Certificado de la Fundación Iberoamericana de Salud Pública.");
    const soldOutMessage =
        "Actualmente no hay cupos disponibles para esta propuesta.";
    const specificFaqGroup = courseSpecificFaqs[curso.url];
    const courseFaqGroups = specificFaqGroup
        ? [specificFaqGroup, ...generalFaqGroups]
        : generalFaqGroups;

    return (
        <div className="curso" ref={pageRef} style={themeStyle}>
            <SiteNavbar
                activeSection="educativo"
                navTheme={curso.theme}
                ctaLabel={isSoldOut ? null : "Inscribite"}
            />

            <section className="hero">
                <div className="hero-pattern"></div>
                <div className="hero-inner">
                    <div className="hero-bread">
                        <Link to="/">Inicio</Link>
                        <span>&nbsp;&rarr;&nbsp;</span>
                        <Link to="/educativo">FISP Educativo</Link>
                        <span>&nbsp;&rarr;&nbsp;</span>
                        <Link to={breadcrumbLink}>{typeConfig.categoryLabel}</Link>
                        <span>&nbsp;&rarr;&nbsp;</span>
                        <span>{displayCourse.nombre}</span>
                    </div>

                    <div className="hero-top">
                        <div className="hero-content">
                            {showHeroChips ? (
                                <div className="hero-chips">
                                    <span
                                        className={`hero-chip hero-chip-primary${
                                            showEnrollmentStatusChip
                                                ? " hero-chip-enrollment"
                                                : ""
                                        }`}
                                    >
                                        {heroPrimaryChipLabel}
                                    </span>
                                    {!showEnrollmentStatusChip ? (
                                        <>
                                            <span className="hero-chip hero-chip-soft">
                                                {displayCourse.modalidad}
                                            </span>
                                            <span className="hero-chip hero-chip-soft">
                                                {displayCourse.titulacion}
                                            </span>
                                        </>
                                    ) : null}
                                </div>
                            ) : null}

                            <h1>{displayCourse.nombre}</h1>
                            <p className="hero-desc">
                                {displayCourse.descripcionPreview ||
                                    displayCourse.descripcion}
                            </p>

                            <div className="hero-btns">
                                {isSoldOut ? (
                                    <span className="btn-w is-sold-out" aria-disabled="true">
                                        Cupos llenos
                                    </span>
                                ) : (
                                    <Link to={enrollmentPath} className="btn-w">
                                        Ver precios y formas de pago
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                        >
                                            <path d="M5 12h14M12 5l7 7-7 7"></path>
                                        </svg>
                                    </Link>
                                )}
                                <a
                                    href="https://wa.me/5491123879118"
                                    className="btn-ghost"
                                >
                                    Consultá por WhatsApp
                                </a>
                            </div>
                        </div>

                        <div className="hero-card">
                            <h3>{dataCardTitle}</h3>
                            <div className="hero-card-grid">
                                {heroFacts.map(([icon, label, value]) => (
                                    <div className="hc-item" key={label}>
                                        <span className="hc-icon" aria-hidden="true">{icon}</span>
                                        <span className="hc-copy">
                                            <span className="label">{label}</span>
                                            <span className="value">{value}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="info-bar">
                <div className="info-bar-inner">
                    {infoBarItems.map((item) => (
                        <span key={item} className="info-bar-item">{item}</span>
                    ))}
                </div>
            </div>

            <div className="main">
                <div className="content">
                    <section className="section section-plain about-section reveal">
                        <span className="section-tag">
                            {coursePage.aboutTag || "Presentación"}
                        </span>
                        <h2 className="section-title">
                            {coursePage.aboutTitle || typeConfig.aboutLabel}
                        </h2>
                        <p className="section-text">{displayCourse.descripcion}</p>
                        {hasExtraDescription
                            ? displayCourse.descripcionExtra.map(
                                  (paragraph, index) => (
                                      <p
                                          key={`${index}-${paragraph}`}
                                          className="section-text"
                                      >
                                          {paragraph}
                                      </p>
                                  )
                              )
                            : null}
                        {isAsyncModality ? (
                            <div className="async-note">
                                <span className="async-note-icon" aria-hidden="true">Ⅱ</span>
                                <span className="async-note-copy">
                                    <strong>Modalidad 100% asincrónica · Sin fecha de inicio fija</strong>
                                    <span>
                                        Sin clases en vivo ni horarios fijos. Accedés al contenido cuando quieras y avanzás a tu propio ritmo.
                                    </span>
                                </span>
                            </div>
                        ) : null}
                    </section>

                    {hasObjectives ? (
                        <section className="section section-plain objectives-section reveal">
                            <span className="section-tag">
                                {coursePage.objectivesTag ||
                                    `Objetivo del ${typeConfig.singularLabel}`}
                            </span>
                            <h2 className="section-title">Qué vas a lograr</h2>
                            <ul className="objectives-list">
                                {displayCourse.objetivos.map((objective) => (
                                    <li key={objective}>{objective}</li>
                                ))}
                            </ul>
                        </section>
                    ) : null}

                    {curso.hasMediaAssets && curso.video ? (
                        <section className="section reveal">
                            <span className="section-tag">Video informativo</span>
                            <h2 className="section-title">
                                Conocé más sobre esta propuesta
                            </h2>
                            <div className="video-wrap">
                                <video controls preload="metadata">
                                    <source src={curso.video} type="video/mp4" />
                                </video>
                            </div>
                        </section>
                    ) : null}

                    {curso.temario ? (
                        <section className="section section-plain temario-section reveal">
                            <div className="temario-panel">
                                <Temarios
                                    temario={curso.temario}
                                    modalidad={displayCourse.modalidad}
                                    course={curso}
                                />
                            </div>
                        </section>
                    ) : null}

                    <section className="section section-plain reveal course-method-section">
                        <span className="section-tag">Metodología</span>
                        <h2 className="section-title">Cómo se cursa</h2>
                        <div
                            className={`course-method-grid${
                                isAsyncModality ? " is-async" : ""
                            }`}
                        >
                            {methodCards.map((card) => (
                                <article className="course-method-card" key={card.title}>
                                    <span className="course-method-icon" aria-hidden="true">{card.icon}</span>
                                    <h3>{card.title}</h3>
                                    <p>{card.text}</p>
                                </article>
                            ))}
                        </div>
                    </section>

                    <section className="section section-plain reveal evaluation-section">
                        <span className="section-tag">Evaluación</span>
                        <h2 className="section-title">Sistema de evaluación</h2>
                        <ul className="evaluation-list">
                            {evaluationItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="section section-plain reveal audience-section">
                        <span className="section-tag">
                            {coursePage.audienceTag || "A quién está dirigido"}
                        </span>
                        <h2 className="section-title">Perfil del aspirante</h2>
                        {/* <div className="audience-stack"> */}
                            <article className="audience-block audience-block-main">
                                {/* <h3>Perfil recomendado</h3> */}
                                <p className="audience-copy">
                                    {displayCourse.destinatarios}
                                </p>
                                {showAudienceTags ? (
                                    <div className="audience-tags">
                                        {displayCourse.destinatarioTags.map((tag) => (
                                            <span className="audience-tag" key={tag}>
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                            </article>
                            {/* <article className="audience-block audience-block-secondary">
                                <div className="audience-block-head">
                                    <h3>Requisitos</h3>
                                </div>
                                {hasRequirements ? (
                                    <ul className="audience-checklist">
                                        {displayCourse.requisitos
                                            .filter(Boolean)
                                            .map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                    </ul>
                                ) : (
                                    <p className="audience-empty">
                                        No se requieren requisitos formales
                                        previos para comenzar.
                                    </p>
                                )}
                            </article> */}
                        {/* </div> */}
                    </section>
                </div>

                <aside className="sidebar reveal">
                    <div className="sidebar-stack">
                        <div className="sidebar-card sidebar-media-card">
                            {showCourseMedia ? (
                                <div className="cover-wrap">
                                    <img
                                        src={curso.foto}
                                        alt={displayCourse.nombre}
                                        className="cover"
                                    />
                                </div>
                            ) : (
                                <div className="cover-fallback">
                                    <span className="cover-badge">
                                        {curso.tipo}
                                    </span>
                                    <h3>{displayCourse.nombre}</h3>
                                </div>
                            )}
                        </div>

                        {directorNames.length ? (
                            <div className="sidebar-card director-card">
                                <h3>Dirección académica</h3>
                                <div className="director-block">
                                    {directorImage ? (
                                        <div className="director-avatars">
                                            {curso.directorImg
                                                .slice(0, 3)
                                                .map((img, index) => (
                                                    <img
                                                        key={`${curso.id}-director-${index}`}
                                                        src={img}
                                                        alt={
                                                            directorNames[index] ||
                                                            "Dirección académica"
                                                        }
                                                    />
                                                ))}
                                        </div>
                                    ) : (
                                        <span className="director-initials">
                                            {getInitials(directorNames[0])}
                                        </span>
                                    )}
                                    <div className="director-copy">
                                        {directorPeople.map((person) => (
                                            <div className="director-person" key={person.nombre}>
                                                <p>{person.nombre}</p>
                                                {person.detalle ? (
                                                    <span>{person.detalle}</span>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {codirectorPeople.length ? (
                            <div className="sidebar-card faculty-card">
                                <h3>Co-dirección académica</h3>
                                <div className="faculty-list">
                                    {codirectorPeople.map((person) => (
                                        <div className="faculty-person" key={person.nombre}>
                                            <p className="faculty-name">{person.nombre}</p>
                                            {person.detalle ? (
                                                <p className="faculty-detail">{person.detalle}</p>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {facultyPeople.length ? (
                            <div className="sidebar-card faculty-card faculty-team-card">
                                <h3>Equipo docente</h3>
                                <div className="fact-list ficha-fact-list">
                                    {facultyPeople.map((person) => {
                                        const nameParts = splitParentheticalNote(person.nombre);

                                        return (
                                            <div
                                                className="fact"
                                                key={`${person.titulo || "docente"}-${person.nombre}`}
                                            >
                                                <span className="fact-label">
                                                    {person.titulo || "Docente"}
                                                </span>
                                                <span className="fact-value">
                                                    <span>
                                                        {nameParts.main}
                                                        {nameParts.note ? (
                                                            <span className="faculty-note">
                                                                {nameParts.note}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                    {person.detalle ? (
                                                        <span className="faculty-detail">
                                                            {person.detalle}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}

                        <div className="sidebar-card certification-card">
                            <div
                                className={`certification-logos ${
                                    isFispUbaCertification ? "has-uba" : ""
                                }`}
                            >
                                <img
                                    src={fispLogo}
                                    alt="FISP"
                                    className="certification-logo certification-logo-fisp"
                                />
                                {isFispUbaCertification ? (
                                    <img
                                        src={ubaLogo}
                                        alt="Universidad de Buenos Aires"
                                        className="certification-logo certification-logo-uba"
                                    />
                                ) : null}
                            </div>
                            <h3>{certificationTitle}</h3>
                            {coursePage.certificationDescription ? (
                                <p>{certificationDescription}</p>
                            ) : certificationOptions.length > 1 ? (
                                <div className="certification-options">
                                    {certificationOptions.map((option) => (
                                        <p key={option.certificacion}>
                                            <strong>{option.certificacion}</strong>
                                            <span>{option.aplicaA}</span>
                                        </p>
                                    ))}
                                </div>
                            ) : (
                                <p>
                                    {isFispUbaCertification
                                        ? "Certificado de la Fundación Iberoamericana de Salud Pública y la Universidad de Buenos Aires."
                                        : "Certificado de la Fundación Iberoamericana de Salud Pública."}
                                </p>
                            )}
                        </div>

                        <div className="sidebar-card sidebar-ficha-card">
                            <h3>{coursePage.sidebarFactsTitle || "Ficha del curso"}</h3>
                            <div className="fact-list ficha-fact-list">
                                {fichaFacts.map(([label, value]) => (
                                    <div className="fact" key={label}>
                                        <span className="fact-label">{label}</span>
                                        <span className="fact-value">{value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="ficha-actions">
                                {isSoldOut ? (
                                    <span
                                        className="ficha-btn ficha-btn-primary is-sold-out"
                                        aria-disabled="true"
                                    >
                                        Cupos llenos
                                    </span>
                                ) : (
                                    <Link to={enrollmentPath} className="ficha-btn ficha-btn-primary">
                                        Ver precios y formas de pago
                                    </Link>
                                )}
                                <a
                                    href="https://wa.me/5491123879118"
                                    className="ficha-btn ficha-btn-ghost"
                                >
                                    <span
                                        className="ficha-whatsapp-dot"
                                        aria-hidden="true"
                                    ></span>
                                    Consultá por WhatsApp
                                </a>
                                {isSoldOut ? (
                                    <p className="status-note">
                                        {soldOutMessage}
                                    </p>
                                ) : null}
                                {coursePage.sidebarNote ? (
                                    <p className="ficha-note">{coursePage.sidebarNote}</p>
                                ) : null}
                            </div>
                        </div>

                        {hasPricing ? (
                            <div className="sidebar-card pricing-card">
                                <div className="pricing">
                                    <h3>Aranceles</h3>
                                    {displayCourse.cuotas > 1 &&
                                    curso.precioSuscripcionMP ? (
                                        <p>
                                            {displayCourse.cuotas} cuotas de{" "}
                                            {formatCurrency(
                                                curso.precioSuscripcionMP
                                            )}
                                        </p>
                                    ) : null}
                                    {curso.precioTransferencia ? (
                                        <p>
                                            Transferencia:{" "}
                                            {formatCurrency(
                                                curso.precioTransferencia
                                            )}
                                        </p>
                                    ) : null}
                                    {curso.precioTransferenciaUSA ? (
                                        <p>
                                            Exterior:{" "}
                                            {formatCurrency(
                                                curso.precioTransferenciaUSA,
                                                "USD"
                                            )}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        <div className="sidebar-card sidebar-action-card">
                            <div className="sidebar-actions">
                                {curso.hasMediaAssets && curso.brochure ? (
                                    <a
                                        href={curso.brochure}
                                        className="sidebar-btn sidebar-btn-primary"
                                        download
                                    >
                                        Descargar brochure
                                    </a>
                                ) : null}
                                {isSoldOut ? (
                                    <span
                                        className="sidebar-btn sidebar-btn-secondary is-sold-out"
                                        aria-disabled="true"
                                    >
                                        Cupos llenos
                                    </span>
                                ) : (
                                    <Link to={enrollmentPath} className="sidebar-btn sidebar-btn-secondary">
                                        Ver precios y formas de pago
                                    </Link>
                                )}
                                <a
                                    href="https://wa.me/5491123879118"
                                    className="sidebar-btn sidebar-btn-ghost"
                                >
                                    WhatsApp
                                </a>
                            </div>

                            {isSoldOut ? (
                                <p className="status-note">
                                    {soldOutMessage}
                                </p>
                            ) : null}
                        </div>

                    </div>
                </aside>
            </div>

            <CourseFaq groups={courseFaqGroups} />

            <section className="course-final-cta reveal">
                <div className="course-final-cta-inner">
                    <h2>Empezá {finalCtaArticle} {displayCourse.nombre}</h2>
                    <div className="course-final-cta-actions">
                        {isSoldOut ? (
                            <span
                                className="course-final-cta-primary is-sold-out"
                                aria-disabled="true"
                            >
                                Cupos llenos
                            </span>
                        ) : (
                        <Link
                            to={enrollmentPath}
                            className="course-final-cta-primary"
                        >
                            Ver precios y formas de pago
                            <span aria-hidden="true">→</span>
                        </Link>
                        )}
                        <Link
                            to={breadcrumbLink}
                            className="course-final-cta-secondary"
                        >
                            Ver todos los programas
                        </Link>
                    </div>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}
